import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import {
  list_call_logs,
  get_attributes_by_engagement_id,
  get_data_to_download_chunked,
} from "../../api/AdminApi";

import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";
import { Table, Modal, Typography, Breadcrumb, List, message } from "antd";

import moment from "moment-timezone";
// import { Button } from "../../components/ui/button";
import { format } from "date-fns";
import { CalendarIcon, CheckIcon } from "@heroicons/react/16/solid";
import { cn } from "../../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";

import { Button } from "../../components/ui/button";
import { Calendar } from "../../components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import { ChevronsUpDown, Download, RefreshCw } from "lucide-react";
import {
  list_customers_report,
  list_interpreters_report,
} from "../../api/AdminApi";
import { GlobalContext } from "../../App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const DragIndexContext = createContext({ active: -1, over: -1 });
const dragActiveStyle = (dragState, id) => {
  const { active, over, direction } = dragState;
  // drag active style
  let style = {};
  if (active && active === id) {
    style = { backgroundColor: "gray", opacity: 0.5 };
  }
  // dragover dashed style
  else if (over && id === over && active !== over) {
    style =
      direction === "right"
        ? { borderRight: "1px dashed gray" }
        : { borderLeft: "1px dashed gray" };
  }
  return style;
};
const TableBodyCell = (props) => {
  const dragState = useContext(DragIndexContext);
  return (
    <td
      {...props}
      style={Object.assign(
        Object.assign({}, props.style),
        dragActiveStyle(dragState, props.id)
      )}
    />
  );
};

const TableHeaderCell = (props) => {
  const dragState = useContext(DragIndexContext);
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: props.id,
  });
  const style = Object.assign(
    Object.assign(
      Object.assign(Object.assign({}, props.style), { cursor: "move" }),
      isDragging
        ? { position: "relative", zIndex: 9999, userSelect: "none" }
        : {}
    ),
    dragActiveStyle(dragState, props.id)
  );
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const CallsReport = () => {
  const { languages, setToastMessage, setToastSeverity, setToastOpen } =
    useContext(GlobalContext);
  const [timezone, setTimezone] = useState("America/New_York"); // Default to EST

  // Helper function to get today's date range in selected timezone
  const getTodayDateRange = (tz = timezone) => ({
    from: moment.tz(moment().format("YYYY-MM-DD"), tz).startOf("day").toDate(),
    to: moment.tz(moment().format("YYYY-MM-DD"), tz).endOf("day").toDate(),
  });
  const [date, setDate] = useState(() => getTodayDateRange("America/New_York"));
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  var _a;
  const [dragIndex, setDragIndex] = useState({ active: -1, over: -1 });
  const [rowCount, setRowCount] = useState(20);
  const [openSections, setOpenSections] = useState({
    customer: false,
    interpreter: false,
    language: false,
  });
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedInterpreters, setSelectedInterpreters] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  // State for call logs data
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });

  // Function to manually refresh data
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCallLogs();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
      }, 500); // Show refresh animation for at least 500ms for better UX
    }
  };

  // Function to clear all filters
  const clearAllFilters = () => {
    setDate(getTodayDateRange());
    setSelectedCustomers([]);
    setSelectedInterpreters([]);
    setSelectedLanguages([]);
  };

  // Function to fetch call logs - memoized to avoid stale closures in polling
  const fetchCallLogs = useCallback(async () => {
    setApiLoading(true);
    try {
      // Prepare filters
      const filters = {};

      if (date?.from && moment(date.from).isValid()) {
        filters.start_date = moment
          .tz(moment(date.from).format("YYYY-MM-DD"), timezone)
          .startOf("day")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      if (date?.to && moment(date.to).isValid()) {
        filters.end_date = moment
          .tz(moment(date.to).format("YYYY-MM-DD"), timezone)
          .add(1, "day") // move to next day
          .startOf("day") // 00:00 in EST
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      // Add other filters if selected
      if (selectedCustomers.length > 0) {
        filters.customers = selectedCustomers.join(",");
      }
      if (selectedInterpreters.length > 0) {
        filters.interpreters = selectedInterpreters.join(",");
      }
      if (selectedLanguages.length > 0) {
        filters.languages = selectedLanguages.join(",");
      }
      filters.page = pagination.current;
      filters.page_size = pagination.pageSize;
      setLoading(true);
      const response = await list_call_logs(filters);
      setCallLogs(response.data.items || []);

      // Set the total count for frontend pagination
      setPagination((prev) => ({
        ...prev,
        total: response.data.meta.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching call logs:", error);
    } finally {
      setLoading(false);
      setApiLoading(false);
    }
  }, [
    date,
    selectedCustomers,
    selectedInterpreters,
    selectedLanguages,
    pagination.current,
    pagination.pageSize,
  ]);

  // Setup polling - always use the latest filters via memoized fetchCallLogs
  useEffect(() => {
    if (apiLoading) return;
    const pollingInterval = setInterval(() => {
      fetchCallLogs();
    }, 60000);
    return () => clearInterval(pollingInterval);
  }, [fetchCallLogs, apiLoading]);

  // Update date range when timezone changes (if current date is today)
  useEffect(() => {
    const today = moment().format("YYYY-MM-DD");
    const currentFromDate = moment(date.from).format("YYYY-MM-DD");
    const currentToDate = moment(date.to).format("YYYY-MM-DD");

    // If both from and to dates are today, update them to today in the new timezone
    if (currentFromDate === today && currentToDate === today) {
      setDate(getTodayDateRange());
    }
  }, [timezone]);

  useEffect(() => {
    console.log(
      "From:",
      moment.tz(date.from, "America/New_York").format("YYYY-MM-DD HH:mm:ss z")
    );
    console.log(
      "To:",
      moment.tz(date.to, "America/New_York").format("YYYY-MM-DD HH:mm:ss z")
    );
    fetchCallLogs();
  }, [fetchCallLogs]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({ title: "", content: "" });

  const [attributesModalVisible, setAttributesModalVisible] = useState(false);
  const [attributesLoading, setAttributesLoading] = useState(false);
  const [attributesList, setAttributesList] = useState([]);
  const [currentEngagementId, setCurrentEngagementId] = useState(null);

  const [videoFieldsModalVisible, setVideoFieldsModalVisible] = useState(false);
  const [currentVideoFields, setCurrentVideoFields] = useState([]);

  // Function to fetch and show attributes
  const showAttributes = async (engagement_id) => {
    setCurrentEngagementId(engagement_id);
    setAttributesModalVisible(true);
    setAttributesLoading(true);

    try {
      const response = await get_attributes_by_engagement_id(engagement_id);
      setAttributesList(response || []);
    } catch (error) {
      console.error("Error fetching attributes:", error);
      setAttributesList([]);
    } finally {
      setAttributesLoading(false);
    }
  };

  // Define columns with access to the showRemarks function
  const baseColumns = [
    {
      title: "Start Time",
      dataIndex: "engagement_start_ts",
      width: 170,
      fixed: "left",
      render: (text) => formatDate(text, timezone),
    },
    {
      title: "End Time",
      dataIndex: "engagement_end_ts",
      width: 170,
      render: (text) => formatDate(text, timezone),
      fixed: "left",
    },
    {
      title: "Engagement ID",
      dataIndex: "engagement_id",
      width: 150,
    },
    { title: "Direction", dataIndex: "engagement_direction", width: 80 },
    { title: "Dialed Number", dataIndex: "dialed_number", width: 120 },
    { title: "Customer", dataIndex: "customer_name", width: 170 },
    { title: "Access Code", dataIndex: "access_code", width: 90 },
    { title: "Language", dataIndex: "language", width: 100 },
    { title: "Channel", dataIndex: "channel", width: 80 },
    { title: "Queue Name", dataIndex: "queue_name", width: 150 },
    // { title: "Queue ID", dataIndex: "queue_id", width: 120 },
    { title: "CC Queue ID", dataIndex: "cc_queue_id", width: 120 },
    {
      title: "Agent Answered",
      dataIndex: "agent_answered_display_name",
      width: 150,
    },
    {
      title: "Interpreter",
      dataIndex: "interpreter_answered_display_name",
      width: 150,
    },
    {
      title: "Interpreter ID",
      dataIndex: "interpreter_answered_s_id",
      width: 150,
    },
    { title: "Caller Number", dataIndex: "caller_number", width: 150 },
    {
      title: "Interpreters Dialed",
      dataIndex: "interpreter_ids_dialed",
      width: 120,
    },
    {
      title: "Interpreters Dialed By Agents",
      dataIndex: "interpreter_ids_dialed_by_agents",
      width: 120,
    },
    { title: "Agents Dialed", dataIndex: "agent_ids_dialed", width: 120 },
    {
      title: "Category Code Value",
      dataIndex: "category_code_value",
      width: 150,
    },
    {
      title: "Category Code Name",
      dataIndex: "category_code_name",
      width: 150,
    },
    {
      title: "Agent Remarks",
      dataIndex: "agent_remarks",
      width: 150,
      render: (text) => (text ? text.replace(/<[^>]*>/g, "") : "-"),
    },
    {
      title: "Interpreter Remarks",
      dataIndex: "interpreter_remarks",
      width: 150,
      render: (text) => (text ? text.replace(/<[^>]*>/g, "") : "-"),
    },
    {
      title: "Queue Time (sec)",
      dataIndex: "queue_duration_s",
      width: 80,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    {
      title: "Duration (sec)",
      dataIndex: "interpretation_duration_s",
      width: 80,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    {
      title: "Handle Time (sec)",
      dataIndex: "user_handle_duration_s",
      width: 80,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    {
      title: "Answer Time (sec)",
      dataIndex: "user_answer_duration_s",
      width: 80,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    {
      title: "Conn Time (sec)",
      dataIndex: "connection_duration_s",
      width: 80,
      render: (text) => (text ? parseFloat(text).toFixed(1) : "0"),
    },
    { title: "Status", dataIndex: "engagement_status", width: 120 },
    { title: "Agent Disposition", dataIndex: "agent_disposition", width: 150 },
    {
      title: "Interpreter Disposition",
      dataIndex: "interpreter_disposition",
      width: 180,
    },
    {
      title: "Payout",
      dataIndex: "interpreter_bill",
      width: 130,
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    {
      title: "Bill Rate",
      dataIndex: "customer_bill_rate",
      width: 100,
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    {
      title: "Total Cost",
      dataIndex: "customer_bill",
      width: 100,
      render: (text) => (text ? `$${parseFloat(text).toFixed(2)}` : "-"),
    },
    {
      title: "Video Fields",
      dataIndex: "video_fields",
      key: "video_fields",
      width: 120,
      render: (text, record) => (
        <Button
          variant="ghost"
          className="p-0 h-auto"
          onClick={() => {
            let raw = record.video_fields;
            if (typeof raw === "string") {
              try {
                raw = JSON.parse(raw);
              } catch {
                raw = [];
              }
            }
            setCurrentVideoFields(Array.isArray(raw) ? raw : []);
            setVideoFieldsModalVisible(true);
          }}
        >
          View Video Fields
        </Button>
      ),
    },
    {
      title: "Fields",
      dataIndex: "fields",
      key: "fields",
      width: 120,
      render: (text, record) => (
        <Button
          variant="ghost"
          className="p-0 h-auto"
          onClick={() => showAttributes(record.engagement_id)}
        >
          View Attributes
        </Button>
      ),
    },
  ];

  const [columns, setColumns] = useState(() =>
    baseColumns.map((column, i) => ({
      ...column,
      key: `${i}`,
      onHeaderCell: () => ({ id: `${i}` }),
      onCell: () => ({ id: `${i}` }),
    }))
  );

  useEffect(() => {
    setColumns(
      baseColumns.map((column, i) => ({
        ...column,
        key: `${i}`,
        onHeaderCell: () => ({ id: `${i}` }),
        onCell: () => ({ id: `${i}` }),
      }))
    );
  }, [timezone]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    })
  );

  const onDragEnd = ({ active, over }) => {
    if (active.id !== (over === null || over === void 0 ? void 0 : over.id)) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex(
          (i) =>
            i.key ===
            (active === null || active === void 0 ? void 0 : active.id)
        );
        const overIndex = prevState.findIndex(
          (i) => i.key === (over === null || over === void 0 ? void 0 : over.id)
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }) => {
    const activeIndex = columns.findIndex((i) => i.key === active.id);
    const overIndex = columns.findIndex(
      (i) => i.key === (over === null || over === void 0 ? void 0 : over.id)
    );
    setDragIndex({
      active: active.id,
      over: over === null || over === void 0 ? void 0 : over.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  // Function to convert data to CSV and download it
  const downloadCSV = async () => {
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      // Prepare filters
      const filters = {};

      if (date?.from && moment(date.from).isValid()) {
        filters.start_date = moment
          .tz(moment(date.from).format("YYYY-MM-DD"), timezone)
          .startOf("day")
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      if (date?.to && moment(date.to).isValid()) {
        filters.end_date = moment
          .tz(moment(date.to).format("YYYY-MM-DD"), timezone)
          .add(1, "day") // move to next day
          .startOf("day") // 00:00 in EST
          .utc()
          .format("YYYY-MM-DD HH:mm:ss");
      }

      // Add other filters if selected
      if (selectedCustomers.length > 0) {
        filters.customers = selectedCustomers.join(",");
      }
      if (selectedInterpreters.length > 0) {
        filters.interpreters = selectedInterpreters.join(",");
      }
      if (selectedLanguages.length > 0) {
        filters.languages = selectedLanguages.join(",");
      }

      // Use chunked download with progress callback
      let response = await get_data_to_download_chunked(filters, (count) => {
        setDownloadProgress(count);
      });
      // Get visible columns (in the current order)
      const visibleColumns = columns;

      // Create CSV header row
      const header = visibleColumns.map((col) => `"${col.title}"`).join(",");

      // Format dates in the CSV data
      const formattedData = response.data.map((item) => ({
        ...item,
        engagement_start_ts: formatDateForCSV(
          item.engagement_start_ts,
          timezone
        ),
        engagement_end_ts: formatDateForCSV(item.engagement_end_ts, timezone),
        // Add other date fields here if needed
      }));

      // Create CSV data rows
      const csvRows = formattedData.map((row) => {
        return visibleColumns
          .map((column) => {
            // Handle special cases for columns with render functions
            const value = row[column.dataIndex];
            // Escape quotes and wrap in quotes to handle commas in content
            return `"${value ? String(value).replace(/"/g, '""') : ""}"`;
          })
          .join(",");
      });

      // Combine header and rows
      const csvContent = [header, ...csvRows].join("\n");

      // Create a Blob with the CSV content
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a download link and trigger the download
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `calls_report_${format(new Date(), "yyyy-MM-dd_HH-mm")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV:", error);
      setToastMessage("Error downloading CSV. Please try again.");
      setToastOpen(true);
      setToastSeverity("error");
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
    }
  };

  const getFilterData = async () => {
    try {
      const [customersResponse, interpretersResponse] = await Promise.all([
        list_customers_report(),
        list_interpreters_report(),
      ]);

      console.log(customersResponse);
      setCustomers(customersResponse.data);

      console.log(interpretersResponse);
      setInterpreters(interpretersResponse.data);
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  };

  useEffect(() => {
    getFilterData();
  }, []);

  // Function to format date based on selected timezone
  const formatDate = (dateString, timezone) => {
    if (!dateString) return "-";
    return moment.utc(dateString).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
  };

  // Function to format date for CSV download (always use UTC)
  const formatDateForCSV = (dateString, timezone) => {
    if (!dateString) return "-";
    return moment.utc(dateString).tz(timezone).format("YYYY-MM-DD HH:mm:ss");
  };

  return (
    <div>
      <Breadcrumb
        className="!mb-6"
        items={[
          {
            title: "Home",
          },
          {
            title: "Interpretation Report",
          },
        ]}
      />
      <div className="grid lg:grid-cols-3 xl:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-10 mb-10">
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Date Range{" "}
            <span className="text-xs text-gray-400">(max 31 days)</span>
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "justify-start text-left font-normal cursor-pointer ",
                  !date && "text-muted-foreground"
                )}
                onClick={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    date: !prev.date,
                  }))
                }
              >
                <CalendarIcon />
                {date?.from ? (
                  date.to ? (
                    <>
                      {format(date.from, "LLL dd, y")} -{" "}
                      {format(date.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(date.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-1 z-50 bg-white" align="center">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                disabled={(calendarDate) => {
                  // If we have a selected start date, disable dates that would make range > 31 days
                  if (
                    date?.from &&
                    !date?.to &&
                    moment(date.from).isValid() &&
                    moment(calendarDate).isValid()
                  ) {
                    const daysDifference = Math.abs(
                      moment(calendarDate).diff(moment(date.from), "days")
                    );
                    return daysDifference > 31;
                  }
                  return false;
                }}
                onSelect={(newDate) => {
                  // Handle case where newDate is undefined/null - default to today
                  if (!newDate) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  // Handle case where from date is cleared - default to today
                  if (!newDate.from) {
                    setDate(getTodayDateRange());
                    return;
                  }

                  // Check if range is more than 31 days (only if both dates exist)
                  if (newDate.to && newDate.from) {
                    const daysDifference = moment(newDate.to).diff(
                      moment(newDate.from),
                      "days"
                    );
                    if (daysDifference > 31) {
                      // Show error message
                      setToastMessage(
                        "Date range cannot exceed 31 days. Please select a smaller range."
                      );
                      setToastOpen(true);
                      setToastSeverity("error");
                      return;
                    }
                  }

                  // Start of day in the selected timezone
                  const fromDate = moment
                    .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                    .toDate();

                  let toDate;
                  if (newDate.to && moment(newDate.to).isValid()) {
                    // End of day in the selected timezone for range
                    toDate = moment
                      .tz(moment(newDate.to).format("YYYY-MM-DD"), timezone)
                      .startOf("day")
                      .toDate();
                  } else {
                    // For single day selection or when to date is invalid, set to end of the same day
                    toDate = moment
                      .tz(moment(newDate.from).format("YYYY-MM-DD"), timezone)
                      .endOf("day")
                      .toDate();
                  }

                  setDate({
                    from: fromDate,
                    to: toDate,
                  });
                }}
                numberOfMonths={1}
              />
            </PopoverContent>
          </Popover>
        </div>
        {/* Customer Dropdown */}
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Customers
          </label>
          <Popover
            open={openSections.customer}
            onOpenChange={(val) =>
              setOpenSections((prev) => ({ ...prev, customer: val }))
            }
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openSections.customer}
                className="justify-between cursor-pointer"
                onClick={() =>
                  setOpenSections((prev) => ({
                    ...prev,
                    customer: !prev.customer,
                  }))
                }
              >
                <span className="line-clamp-1">
                  {selectedCustomers.length
                    ? `${selectedCustomers.length} selected`
                    : "Select customer..."}
                </span>
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command
                filter={(value, search, keywords) => {
                  const q = search.trim().toLowerCase();
                  if (!q) return 1;
                  return keywords.some((k) => {
                    const lower = k.toLowerCase();
                    return lower === q || lower.includes(q);
                  })
                    ? 1
                    : 0;
                }}
              >
                <CommandInput placeholder="Search customer..." />
                <CommandList>
                  <CommandEmpty>No exact match found.</CommandEmpty>
                  <CommandGroup>
                    {customers.map((cust) => (
                      <CommandItem
                        key={cust.customer_id}
                        value={cust.customer_name}
                        keywords={[cust.customer_name, cust.access_code]}
                        onSelect={() => {
                          setSelectedCustomers((prev) =>
                            prev.includes(cust.customer_id)
                              ? prev.filter((v) => v !== cust.customer_id)
                              : [...prev, cust.customer_id]
                          );
                        }}
                        className="cursor-pointer"
                      >
                        {cust.customer_name} -{" "}
                        <span className="font-bold">{cust.access_code}</span>
                        <CheckIcon
                          className={`ml-auto ${
                            selectedCustomers.includes(cust.customer_id)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-1 gap-1 flex flex-row flex-wrap max-h-20 overflow-y-auto items-start justify-start content-start">
            {selectedCustomers.map((customerId, idx) => {
              const customer = customers.find(
                (c) => c.customer_id === customerId
              );
              return (
                <span
                  key={customerId}
                  className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {customer ? customer.customer_name : customerId}
                  <button
                    type="button"
                    className="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20"
                    onClick={() =>
                      setSelectedCustomers((prev) =>
                        prev.filter((id) => id !== customerId)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                    <span className="absolute -inset-1" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        {/* Interpreter Dropdown */}
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Interpreters
          </label>
          <Popover
            open={openSections.interpreter}
            onOpenChange={(val) =>
              setOpenSections((prev) => ({ ...prev, interpreter: val }))
            }
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openSections.interpreter}
                className="justify-between cursor-pointer"
              >
                <span className="line-clamp-1">
                  {selectedInterpreters.length
                    ? `${selectedInterpreters.length} selected`
                    : "Select interpreter..."}
                </span>
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command
                filter={(value, search, keywords) => {
                  const q = search.trim().toLowerCase();
                  if (!q) return 1;
                  return keywords.some((k) => {
                    const lower = k.toLowerCase();
                    return lower === q || lower.includes(q);
                  })
                    ? 1
                    : 0;
                }}
              >
                <CommandInput placeholder="Search interpreter..." />
                <CommandList>
                  <CommandEmpty>No Interpreters found.</CommandEmpty>
                  <CommandGroup>
                    {interpreters.map((int) => (
                      <CommandItem
                        keywords={[
                          int.interpreter_first_name,
                          int.interpreter_last_name,
                        ]}
                        key={int.interpreter_id}
                        value={`${int.interpreter_first_name} ${int.interpreter_last_name} ${int.interpreter_special_id}`}
                        className={"cursor-pointer"}
                        onSelect={() => {
                          setSelectedInterpreters((prev) =>
                            prev.includes(int.interpreter_id)
                              ? prev.filter((v) => v !== int.interpreter_id)
                              : [...prev, int.interpreter_id]
                          );
                        }}
                      >
                        {int.interpreter_first_name +
                          " " +
                          int.interpreter_last_name}
                        {/* <span>-</span>
                        <span className="font-bold">
                          {int.interpreter_special_id}
                        </span> */}
                        <CheckIcon
                          className={`ml-auto ${
                            selectedInterpreters.includes(int.interpreter_id)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-1 gap-1 flex flex-row flex-wrap max-h-20 overflow-y-auto items-start justify-start content-start">
            {selectedInterpreters.map((interpreterId, idx) => {
              const interpreter = interpreters.find(
                (i) => i.interpreter_id === interpreterId
              );
              const interpreterName = interpreter
                ? `${interpreter.interpreter_first_name} ${interpreter.interpreter_last_name}`
                : interpreterId;
              return (
                <span
                  key={interpreterId}
                  className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {interpreterName}
                  <button
                    type="button"
                    className="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20"
                    onClick={() =>
                      setSelectedInterpreters((prev) =>
                        prev.filter((int) => int !== interpreterId)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                    <span className="absolute -inset-1" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        {/* Language Dropdown */}
        <div className="flex flex-col">
          <label className="text-sm text-muted-foreground mb-1">
            Languages
          </label>
          <Popover
            open={openSections.language}
            onOpenChange={(val) =>
              setOpenSections((prev) => ({ ...prev, language: val }))
            }
          >
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={openSections.language}
                className="justify-between cursor-pointer"
              >
                <span className="line-clamp-1">
                  {selectedLanguages.length
                    ? `${selectedLanguages.length} selected`
                    : "Select language..."}
                </span>
                <ChevronsUpDown className="opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0">
              <Command>
                <CommandInput placeholder="Search language..." />
                <CommandList>
                  <CommandEmpty>No Languages found.</CommandEmpty>
                  <CommandGroup>
                    {languages.map((lang) => (
                      <CommandItem
                        key={lang.name}
                        value={lang.name}
                        className={"cursor-pointer"}
                        onSelect={() => {
                          setSelectedLanguages((prev) =>
                            prev.includes(lang.name)
                              ? prev.filter((v) => v !== lang.name)
                              : [...prev, lang.name]
                          );
                        }}
                      >
                        {lang.name}
                        <CheckIcon
                          className={`ml-auto ${
                            selectedLanguages.includes(lang.name)
                              ? "opacity-100"
                              : "opacity-0"
                          }`}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="mt-1 gap-1 flex flex-row flex-wrap max-h-20 overflow-y-auto items-start justify-start content-start">
            {selectedLanguages.map((langCode, idx) => {
              const language = languages.find((l) => l.code === langCode);
              const langName = language ? language.name : langCode;
              return (
                <span
                  key={langCode}
                  className="inline-flex items-center gap-x-0.5 rounded-md bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600"
                >
                  {langName}
                  <button
                    type="button"
                    className="group relative -mr-1 size-3.5 rounded-xs hover:bg-gray-500/20"
                    onClick={() =>
                      setSelectedLanguages((prev) =>
                        prev.filter((lang) => lang !== langCode)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <svg
                      viewBox="0 0 14 14"
                      className="size-3.5 stroke-gray-700/50 group-hover:stroke-gray-700/75"
                    >
                      <path d="M4 4l6 6m0-6l-6 6" />
                    </svg>
                    <span className="absolute -inset-1" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Call Logs</h1>
        <div className="flex space-x-4 items-end">
          <div className="flex flex-col space-x-2">
            <span className="text-sm text-gray-600">Timezone:</span>
            <Select value={timezone} onValueChange={setTimezone}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="America/New_York">
                  Eastern Time (EST)
                </SelectItem>
                <SelectItem value="UTC">UTC</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer border-red-500 text-red-500 hover:bg-red-50"
            onClick={clearAllFilters}
          >
            Clear Filters
          </Button>
          {/* <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={fetchCallLogs}
          >
            Apply Filters
          </Button> */}
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={handleManualRefresh}
            disabled={refreshing}
          >
            <RefreshCw
              className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button
            variant="outline"
            className="flex items-center gap-2 cursor-pointer"
            onClick={downloadCSV}
            disabled={isDownloading}
          >
            <Download className="h-4 w-4" />
            {isDownloading
              ? downloadProgress > 0
                ? `Downloading... (${downloadProgress} records)`
                : "Preparing download..."
              : "Download CSV"}
          </Button>
        </div>
      </div>

      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((i) => i.key)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <Table
              rowHoverable={false}
              size="small"
              scroll={{ x: "max-content", y: "calc(100vh - 500px)" }}
              bordered
              rowKey="engagement_id"
              columns={columns}
              dataSource={callLogs}
              pagination={{
                ...pagination,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `${total} records`,
                pageSizeOptions: ["10", "20", "30", "50", "100"],
                size: "default",
                position: ["bottomRight"],
                defaultPageSize: 30,
                current: pagination.current,
                pageSize: pagination.pageSize,
                onChange: (page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize: pageSize,
                  }));
                },
                onShowSizeChange: (current, size) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: 1, // Reset to first page when changing page size
                    pageSize: size,
                  }));
                },
              }}
              components={{
                header: { cell: TableHeaderCell },
                body: { cell: TableBodyCell },
              }}
            />
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          <th style={{ backgroundColor: "gray", padding: 16 }}>
            {(_a =
              columns[columns.findIndex((i) => i.key === dragIndex.active)]) ===
              null || _a === void 0
              ? void 0
              : _a.title}
          </th>
        </DragOverlay>

        {/* Modal for displaying remarks */}
        <Modal
          title={modalContent.title}
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>,
          ]}
        >
          <Typography.Paragraph>{modalContent.content}</Typography.Paragraph>
        </Modal>

        {/* Modal for displaying attributes */}
        <Modal
          title={`Attributes for Engagement ID: ${currentEngagementId}`}
          open={attributesModalVisible}
          onCancel={() => setAttributesModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setAttributesModalVisible(false)}
            >
              Close
            </Button>,
          ]}
        >
          {attributesLoading ? (
            <div className="flex justify-center items-center py-4">
              Loading attributes...
            </div>
          ) : attributesList.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={attributesList}
              renderItem={(item) => (
                <List.Item>
                  <Typography.Text strong>
                    {item.attribute_name}:
                  </Typography.Text>{" "}
                  {item.attribute_value}
                </List.Item>
              )}
            />
          ) : (
            <Typography.Paragraph>
              No attributes found for this engagement.
            </Typography.Paragraph>
          )}
        </Modal>

        {/* Modal for displaying video fields */}
        <Modal
          title="Video Fields"
          open={videoFieldsModalVisible}
          onCancel={() => setVideoFieldsModalVisible(false)}
          footer={[
            <Button
              key="close"
              onClick={() => setVideoFieldsModalVisible(false)}
            >
              Close
            </Button>,
          ]}
        >
          {currentVideoFields.length > 0 ? (
            <List
              size="small"
              bordered
              dataSource={currentVideoFields.flatMap((item, idx) =>
                item && typeof item === "object"
                  ? Object.entries(item).map(([k, v]) => ({
                      id: `${idx}-${k}`,
                      fieldKey: k,
                      value: v,
                    }))
                  : []
              )}
              renderItem={(item) => (
                <List.Item key={item.id}>
                  <Typography.Text strong>{item.fieldKey}:</Typography.Text>{" "}
                  {item.value != null && item.value !== ""
                    ? String(item.value)
                    : "—"}
                </List.Item>
              )}
            />
          ) : (
            <Typography.Paragraph>
              No video fields for this record.
            </Typography.Paragraph>
          )}
        </Modal>
      </DndContext>
    </div>
  );
};
export default CallsReport;
